import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useFeature } from '@growthbook/growthbook-react';
import GameGridItem from '../Game/GameGridItem/GameGridItem';
import { useGridWidth } from './hooks';
import useIsDesktopLayout from '../../hooks/useIsDesktopLayout';
import { createMockArray } from '../../utils';
import { brandSelector } from '../../redux/reducers/app/params';
import { SCCOM_BRAND } from '../../constants/brands';
import FEATURES from '../../constants/featureToggling';
import styles from './GamesGrid.module.css';
import NavigationArrows from '../Slider/NavigationArrows/NavigationArrows';
import { useElementRef } from '../../hooks/useElementRef';
import { useCardsNavigation } from '../../hooks/useCardsNavigation';

export const SKELETON_GAMES_COUNT = 12;

const GamesGrid = memo(
  ({
    dataAttributes = {},
    isLoading = false,
    games = [],
    scrollable,
    gamesInRaw,
    gamesCount = SKELETON_GAMES_COUNT,
    sectionIndex,
    eventActionPlay,
    eventActionEntityFieldName,
    eventActionEntityFieldValue,
    analyticsData,
    className,
  }) => {
    const [rootElement, rootRef] = useElementRef();
    const [cardElement, cardRef] = useElementRef();
    const gridWidth = useGridWidth(rootRef);

    const { prevSlide, isPrevEnabled, nextSlide, isNextEnabled } =
      useCardsNavigation(rootElement, cardElement?.offsetWidth);

    const shouldUseThreeColumnsForGames = useFeature(
      FEATURES.USE_THREE_COLUMNS_FOR_GAMES
    ).on;
    const isDesktop = useIsDesktopLayout();
    const brand = useSelector(brandSelector);
    const isSCCOM = brand === SCCOM_BRAND;

    return (
      <div className={styles.listWrapper}>
        <div
          style={{
            '--grid-width': gridWidth + 'px',
            ...(isDesktop
              ? { '--games-in-raw': gamesInRaw || gamesCount }
              : {}),
          }}
          className={classNames(
            styles.root,
            scrollable && styles.scrollable,
            shouldUseThreeColumnsForGames && styles.threeColumn,
            isSCCOM && styles.root_sccom,
            className
          )}
          ref={rootRef}
          {...(isLoading ? {} : dataAttributes)}
        >
          {isLoading
            ? createMockArray(gamesCount).map((num) => (
                <GameGridItem key={num} className={styles.gridItem} isLoading />
              ))
            : games.map((game, index) => (
                <GameGridItem
                  ref={cardRef}
                  sectionIndex={sectionIndex}
                  index={index}
                  key={game?.id}
                  isLoading={isLoading}
                  game={game}
                  eventAction={eventActionPlay}
                  eventActionEntityFieldName={eventActionEntityFieldName}
                  eventActionEntityFieldValue={eventActionEntityFieldValue}
                  analyticsData={analyticsData}
                  className={styles.gridItem}
                  imgClassName={styles.gridItemImg}
                />
              ))}
        </div>
        {(!isDesktop || scrollable) && (
          <NavigationArrows
            prevHandler={prevSlide}
            nextHandler={nextSlide}
            wrapperClassName={classNames(styles.navigation)}
            buttonClassName={styles.item}
            isNextEnabled={isNextEnabled}
            isPrevEnabled={isPrevEnabled}
          />
        )}
      </div>
    );
  }
);
GamesGrid.propTypes = {
  dataAttributes: PropTypes.shape({}),
  games: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLoading: PropTypes.bool,
  categoryId: PropTypes.string,
  gamesInRaw: PropTypes.number,
  scrollable: PropTypes.bool,
};

export default GamesGrid;
